import { useEffect, useState } from "react";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  TextField,
  Box,
} from "@mui/material";
import { getInputLabelProps, getInputProps } from "../../../common/appStyles";
import localize from "../../../common/localize";
import Combobox from "../../../components/common/Combobox";
import {
  getPaymentTypeModeLabel,
  MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION,
} from "../../../common/utils";
import NotificationSetup from "../../../components/common/NotificationSetup";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";
import ExpandableView from "../../../components/common/ExpandableView";
import CreatedByText from "../../../components/common/CreatedByText";

export default function AddOrEditAdditionalCostType({ model, setModel }) {
  const [selectedPaymentMode, setSelectedPaymentMode] = useState({
    Id: model.PaymentMode,
    Name: getPaymentTypeModeLabel(model.PaymentMode),
  });
  const getPaymentModes = () => [
    {
      Id: "FIXED",
      Name: getPaymentTypeModeLabel("FIXED"),
    },
    {
      Id: "PERCENTAGE",
      Name: getPaymentTypeModeLabel("PERCENTAGE"),
    },
  ];
  const [descriptionCount, setDescriptionCount] = useState(0);
  const maximumCharactersForDescription =
    MAXIMUM_CHARACTERS_FOR_DESCRIPTION_OR_OBSERVATION;

  useEffect(() => {
    if (selectedPaymentMode) {
      setModel({
        ...model,
        PaymentMode: selectedPaymentMode.Id,
      });
    }
  }, [selectedPaymentMode]);

  useEffect(() => {
    if (model.Description) {
      setDescriptionCount(model.Description.length);
    }
  }, [model?.Description]);

  const handleChange = (e) => {
    if (e.target.name === "PaymentValue") {
      setModel({
        ...model,
        [e.target.name]:
          model.PaymentMode === "PERCENTAGE" && e.target.value < 0
            ? 0
            : model.PaymentMode === "PERCENTAGE" && e.target.value > 100
            ? 100
            : e.target.value,
      });
      return;
    }

    setModel({
      ...model,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckedChanged = (e) => {
    setModel({
      ...model,
      [e.target.name]: e.target.checked,
    });
  };

  const handlePaymentModeChange = (value) => {
    if (!value) {
      setSelectedPaymentMode({
        Id: "FIXED",
        Name: getPaymentTypeModeLabel("FIXED"),
      });

      return;
    }

    if (value.Id === "PERCENTAGE" && model.PaymentValue > 100) {
      setModel({
        ...model,
        PaymentValue: 100,
      });
    }

    setSelectedPaymentMode({
      Id: value.Id,
      Name: getPaymentTypeModeLabel(value.Id),
    });
  };

  return (
    <div style={{ width: "100%" }}>
      <ExpandableView
        components={[
          {
            Name: localize.general_information,
            Component: (
              <form className="w-full flex flex-col gap-3">
                <FormControl required fullWidth sx={{ marginTop: "10px" }}>
                  <TextField
                    required
                    InputLabelProps={getInputLabelProps()}
                    inputProps={getInputProps()}
                    size="small"
                    fullWidth
                    id="outlined-basic"
                    name="Name"
                    label={localize.name}
                    variant="outlined"
                    onChange={handleChange}
                    value={model.Name}
                  />
                </FormControl>

                <FormControl required fullWidth>
                  <TextField
                    InputLabelProps={getInputLabelProps()}
                    inputProps={{
                      style: {
                        fontSize: "0.8em",
                      },
                      maxLength: maximumCharactersForDescription,
                    }}
                    size="small"
                    fullWidth
                    multiline
                    rows={4}
                    id="outlined-basic"
                    name="Description"
                    label={localize.description}
                    variant="outlined"
                    onChange={handleChange}
                    value={model.Description}
                    helperText={`${descriptionCount}/${maximumCharactersForDescription}`}
                  />
                </FormControl>

                <TextField
                  type="number"
                  InputLabelProps={getInputLabelProps()}
                  inputProps={getInputProps()}
                  value={model.DefaultValue}
                  onChange={handleChange}
                  size="small"
                  id="DefaultValue"
                  name="DefaultValue"
                  label={localize.default_value}
                  variant="outlined"
                  fullWidth
                />

                <FormControlLabel
                  size="small"
                  name="GeneratesPaymentToTechnician"
                  control={
                    <Checkbox
                      size="small"
                      name="GeneratesPaymentToTechnician"
                      checked={model.GeneratesPaymentToTechnician}
                      onChange={handleCheckedChanged}
                    />
                  }
                  label={
                    <span style={{ fontSize: "0.8em" }}>
                      {localize.generates_technician_payment}
                    </span>
                  }
                />

                <div className="w-full flex gap-2 items-center justify-between">
                  {model.GeneratesPaymentToTechnician && (
                    <FormControl required sx={{ flex: 0.5 }}>
                      <Combobox
                        isRequired
                        id="PaymentMode"
                        fieldLabel={localize.payment_mode}
                        fieldOptions={getPaymentModes()}
                        fieldValue={selectedPaymentMode}
                        handleValueChange={handlePaymentModeChange}
                      />
                    </FormControl>
                  )}

                  {model.GeneratesPaymentToTechnician && (
                    <TextField
                      type="number"
                      style={{ flex: 0.5 }}
                      InputLabelProps={getInputLabelProps()}
                      inputProps={getInputProps()}
                      value={model.PaymentValue}
                      onChange={handleChange}
                      size="small"
                      id="PaymentValue"
                      name="PaymentValue"
                      label={localize.value}
                      variant="outlined"
                      fullWidth
                    />
                  )}
                </div>
              </form>
            ),
          },
          {
            Name: localize.notification,
            Component: (
              <NotificationSetup
                entity={model}
                setEntity={setModel}
                showOnlyDefinedToUser
                subtitle={localize.notify_additional_cost_type}
              />
            ),
          },
        ]}
      />

      <CreatedByText entity={model} />
    </div>
  );
}
